import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuth } from './context/AuthContext'
import { TrialWallProvider } from './context/TrialWallContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import { RouteFallback } from './components/RouteFallback'

// Lazy-load the heavier app surfaces so the login bundle stays tiny
const Dashboard        = lazy(() => import('./pages/Dashboard'))
const NewCampaign      = lazy(() => import('./pages/NewCampaign'))
const Campaigns        = lazy(() => import('./pages/Campaigns'))
const CopyGenerator    = lazy(() => import('./pages/CopyGenerator'))
const Connections      = lazy(() => import('./pages/Connections'))
const Library          = lazy(() => import('./pages/Library'))
const Inbox            = lazy(() => import('./pages/Inbox'))
const AIAgent          = lazy(() => import('./pages/AIAgent'))
const Profile          = lazy(() => import('./pages/Profile'))
const Billing          = lazy(() => import('./pages/Billing'))
const CampaignDetail   = lazy(() => import('./pages/CampaignDetail'))
const Reports          = lazy(() => import('./pages/Reports'))
const ImageGenerator   = lazy(() => import('./pages/ImageGenerator'))
const Pricing          = lazy(() => import('./pages/Pricing'))
const Register         = lazy(() => import('./pages/Register'))
const Onboarding       = lazy(() => import('./pages/Onboarding'))
const PrivacyPolicy    = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService   = lazy(() => import('./pages/TermsOfService'))
const DataDeletion     = lazy(() => import('./pages/DataDeletion'))
const Admin            = lazy(() => import('./pages/Admin'))
const SeoLanding       = lazy(() => import('./pages/SeoLanding'))
const Referrals        = lazy(() => import('./pages/Referrals'))
const Recursos         = lazy(() => import('./pages/Recursos'))
const StudioSetup      = lazy(() => import('./pages/StudioSetup'))
const StudioInbox      = lazy(() => import('./pages/StudioInbox'))
const StudioCreate      = lazy(() => import('./pages/StudioCreate'))
const StudioPostPreview = lazy(() => import('./pages/StudioPostPreview'))
const LandingPublic     = lazy(() => import('./pages/LandingPublic'))
const Landings          = lazy(() => import('./pages/Landings'))
const LandingNew        = lazy(() => import('./pages/LandingNew'))
const LandingEdit       = lazy(() => import('./pages/LandingEdit'))
const QuizPublic        = lazy(() => import('./pages/QuizPublic'))
const QuizList          = lazy(() => import('./pages/QuizList'))
const QuizEdit          = lazy(() => import('./pages/QuizEdit'))
const CRM               = lazy(() => import('./pages/CRM'))
const BookingPublic     = lazy(() => import('./pages/BookingPublic'))
const Automations       = lazy(() => import('./pages/Automations'))
const ContentResolver   = lazy(() => import('./pages/ContentResolver'))
const HubPage           = lazy(() => import('./pages/HubPage'))

// '/onboarding' DEBE estar aquí: si no, estando ya en /onboarding con
// onboarding_done=false, PrivateRoute redirige a /onboarding → bucle infinito
// (pantalla pegada/azul en blanco). El código original lo eximía; un refactor
// lo perdió y el flag viejo de localStorage lo enmascaraba.
const SKIP_ONBOARDING_PATHS = ['/quizzes', '/onboarding']

function PrivateRoute({ children }) {
  const { user, profile } = useAuth()
  if (!user) return <Navigate to="/login" replace />

  const path = window.location.pathname
  const skipOnboarding = SKIP_ONBOARDING_PATHS.some(p => path.startsWith(p))
  // Solo la DB manda. El antiguo fallback a localStorage hacía que un login
  // nuevo en el mismo navegador (ej. con Google) heredara el "ya hecho" de
  // otra cuenta y se saltara el onboarding.
  const onboardingDone = profile?.onboarding_done === true
  if (!onboardingDone && profile !== null && !skipOnboarding) {
    return <Navigate to="/onboarding" replace />
  }
  return children
}

export default function App() {
  return (
    <TrialWallProvider>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/s/:slug" element={<SeoLanding />} />
        <Route path="/q/:slug" element={<QuizPublic />} />
        <Route path="/p/:slug" element={<LandingPublic />} />
        <Route path="/reserva/:token" element={<BookingPublic />} />
        <Route path="/:slug/reserva" element={<BookingPublic />} />
        <Route path="/p/:slug/:offerSlug" element={<LandingPublic />} />
        <Route path="/recursos" element={<Recursos />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/data-deletion-instructions" element={<DataDeletion />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/new" element={<NewCampaign />} />
          <Route path="campaigns/:id" element={<CampaignDetail />} />
          <Route path="copy-generator" element={<CopyGenerator />} />
          <Route path="connections" element={<Connections />} />
          <Route path="library" element={<Library />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="ai-agent" element={<AIAgent />} />
          <Route path="profile" element={<Profile />} />
          <Route path="billing" element={<Billing />} />
          <Route path="reports" element={<Reports />} />
          <Route path="image-generator" element={<ImageGenerator />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="admin" element={<Admin />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="studio/setup" element={<StudioSetup />} />
          <Route path="studio/photos" element={<StudioInbox />} />
          <Route path="studio/create" element={<StudioCreate />} />
          <Route path="studio/post/:id" element={<StudioPostPreview />} />
          <Route path="landings" element={<Landings />} />
          <Route path="landings/new" element={<LandingNew />} />
          <Route path="landings/:id/edit" element={<LandingEdit />} />
          <Route path="quizzes" element={<QuizList />} />
          <Route path="quizzes/:id/edit" element={<QuizEdit />} />
          <Route path="crm" element={<CRM />} />
          <Route path="automations" element={<Automations />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
        {/* /:clientSlug/:contentSlug — URL canónica para landings y quizzes del dueño.
            Debe ir DESPUÉS de /p/:slug/:offerSlug y /:slug/reserva (rutas de dos segmentos
            específicas) y ANTES de /:slug (SlugRedirect) y * (fallback). */}
        <Route path="/:clientSlug/:contentSlug" element={<ContentResolver />} />
        <Route path="/:slug" element={<HubPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
    </TrialWallProvider>
  )
}
